// src/lib/permissions.js — single source of truth for role → capabilities.
//
// ⚠️ UI gating only (cosmetic for now). The scheduler still writes directly to Supabase
// with the `authenticated` role, and RLS is currently `authenticated_all` — so hiding a
// control prevents ACCIDENTS and structures the app, but is NOT yet a security boundary.
// The money/fleet levers (AI settings → admin_config, version revoke → app_versions) need
// real enforcement (role-scoped RLS or server.js requireCap) before this is trustworthy.
//
// Model: caps is '*' (everything) or an explicit ALLOW-set (additive — a role has a
// capability only if it is listed; there are no deny rules). Admin sub-tabs are gated by
// the capability id `admin.<sub>`. Unknown role / unknown cap → false (least privilege).
//
// ⚠️ `tab.contracts` is in EVERY role set on purpose (owner ruling 2026-09-11: "tab
// ხელშეკრულებები that will be shown to everyone — agents, managers and admin, anyone any
// role"). It is the one tab this file does not narrow. Do NOT "tidy" it out of a role: what
// differs between roles is what the SERVER shows inside it — a viewer without `can_act` on
// the meeting gets `id_masked` and no contact details, and only a `can_act` row offers the
// review-link action. The gate is server-side; this cap is only the door.
// ⚠️ `contracts.agent_filter` — the roster facet in the ხელშეკრულებები sidebar — is granted to
// NOBODY here, which means ADMIN ONLY: admin is '*'. Owner 2026-09-11, in two steps ("agents
// shouldn't have this list", then "only admin sees list like it sees in desk filter"). It gates
// the CONTROL, not the data: the list itself is scoped by `can_act` on the server, so a
// hand-written `agent=` in the URL still returns only contracts the viewer could already see.
// A manager keeps "ჩემი", which needs no roster — the server resolves the caller.
export const CAPS = {
  admin: '*',
  // general_manager ("General Manager", added 2026-07-09 — Tako's role): the FULL access
  // `manager` used to have before it was truncated below, PLUS Desk (added 2026-07-09,
  // same day) — sees Desk exactly like admin (every agent's entries, not just their own;
  // see the role === 'admin' || role === 'general_manager' checks in IntakeTab.jsx). Runs
  // the company.
  general_manager: new Set([
    // top-level tabs — general managers run the company, so all of them
    'tab.datadesk', 'tab.contracts', 'tab.attendance', 'tab.candidates', 'tab.agents', 'tab.listings', 'tab.deals', 'tab.stats', 'tab.admin',
    'stats.general',   // the company-wide money view — see the note on manager below
    // Settings → Notifications (0017): every role connects its own Telegram. See `tab.settings` on manager.
    'tab.settings', 'settings.notifications', 'settings.passwords',
    'stats.managers',  // the Managers tab
    'stats.range.full', // ranges beyond 30 days, and the custom date picker
    'stats.open_any',  // open ANY agent's detail, not only their own
    // admin sub-tabs (reduced set)
    'admin.general',   // offices, config lists, performance weights, late threshold
    'admin.phones',    // phone inventory
    'admin.ext',       // extension config
    'admin.monitor',   // schema-drift monitor
    'admin.notifications', // Telegram notifications — reached here, so Settings is not shown to this role
    'agents.change_office', // reassign an agent's office in AgentModal — see cap notes below
    // 0015 — number edits (plan, SIM, ban, note, add) and agent hand-out; NOT phones.move_office.
    'phones.edit', 'phones.assign_agent',
    'offices.edit_schedule', 'offices.connect_router',
    'agents.assign_privileged_role', // Manager/Admin/Agent +HR/General Manager job titles
  ]),
  // manager ("Manager", truncated 2026-07-09): Desk + 5 day-to-day tabs only — no Stats,
  // no Admin at all. Deliberately narrower than general_manager now. Desk itself is also
  // narrower: IntakeTab scopes manager (like agent_hr/agent) to their OWN entries only —
  // only admin/general_manager get the full company-wide Desk view.
  manager: new Set([
    'tab.datadesk', 'tab.contracts', 'tab.attendance', 'tab.candidates', 'tab.agents', 'tab.listings', 'tab.deals',
    'listings.lock',
    // Statistics, OFFICE-SCOPED (owner ruling 2026-08-29). A manager earns a share of every
    // deal their office closes and could not see a single one of those numbers, including
    // their own. Granting the tab is safe here and nowhere else, because the roster this tab
    // receives is `filteredAgents` — already force-locked to the manager's own office in
    // App.jsx, with no picker to widen it. What they gain is their team; what they still
    // cannot reach is another office.
    'tab.stats',
    'stats.managers',   // managers see each other (owner, 2026-08-29)
    'stats.range.full',
    'stats.open_any',   // a manager may open anyone in their office
    // …but NOT the company view. `stats.general` is company-wide gross, agent, manager and
    // retained money across every office, which is not a manager's to read. Absence of a cap
    // is a denial here (CAPS is allow-list only), so this needs no counterpart anywhere.
    // Settings (owner 2026-09-16): the SAME Numbers and Office Configuration surfaces Admin
    // uses, scoped to the offices this manager covers (live office_manager rows). The scope and
    // every field rule are enforced by the scheduler DB (0015 RLS + triggers); these caps only
    // decide which controls render. Deliberately absent: phones.move_office, phones.edit,
    // offices.edit_core, offices.edit_security.
    'tab.settings', 'settings.numbers', 'settings.offices', 'settings.notifications', 'settings.passwords',
    'phones.assign_agent', 'offices.edit_schedule',
    // Owner 2026-09-22: "make that true for managers too" — Connect to router for THEIR offices.
    // Which offices is the database's call (office_reporter_can_manage, scheduler 0034).
    'offices.connect_router',
  ]),
  // agent: Desk only, scoped to their own entries (server-side filter by created_by_agent_id
  // in IntakeTab — this cap just controls tab visibility, same UI-only caveat as the header).
  // + Listings READ-ONLY (2026-07-22): posting-activity timeline for their office, WITHOUT
  // `listings.lock` — see the cap note below.
  // 'tab.attendance.self' (owner 2026-09-02): the agent's OWN attendance, which is a
  // different surface from 'tab.attendance' — see MyAttendance.jsx. Granting the manager
  // tab instead would hand agents the roster grid, the schedule, overrides and colleagues'
  // work numbers, which AttendanceTab's own comments say are renderable there only because
  // agents cannot reach it.
  agent: new Set(['tab.datadesk', 'tab.contracts', 'tab.listings', 'tab.attendance.self',
    // Settings → Notifications only (owner 2026-09-16: "add the config tab for agents too"). What an
    // agent's Telegram carries is their own clients and deals — decided server-side (0017 + notifier).
    'tab.settings', 'settings.notifications', 'settings.passwords',
    // Statistics, heavily narrowed (owner ruling 2026-08-29). An agent sees the ACTIVE roster
    // and can open only THEIR OWN detail — everyone else is a row they cannot click. They also
    // cannot reach `stats.managers` (no manager tab), `stats.general` (no company money) or
    // `stats.range.full` (capped to the last 30 days).
    'tab.stats']),
  // agent_hr ("Agent +HR", added 2026-07-07): the agent Desk view PLUS the Candidates
  // (hiring) tab and the Agents roster tab — for staff who recruit and manage staff
  // alongside normal agent work. Note: NOT granted `agents.link_login`, so the Agents
  // tab still hides the "Scheduler login" identity picker for this role.
  // agent_hr is an agent PLUS hiring duties, so it gets the same narrowed Statistics an
  // agent gets — own detail only, 30-day cap, no managers or company view. Withholding it
  // would leave the one role that recruits unable to see its own numbers.
  agent_hr: new Set(['tab.datadesk', 'tab.contracts', 'tab.candidates', 'tab.agents', 'tab.listings', 'tab.stats', 'tab.attendance.self',
    'tab.settings', 'settings.notifications', 'settings.passwords']),
  // Top-level tabs gate on `tab.<id>` (attendance/candidates/agents/listings/deals/stats/admin/datadesk).
  // datadesk (the former Admin → Intake sub-tab): everyone has the TAB now (admin,
  // general_manager, manager, agent_hr, agent) — what differs is the SCOPE within it, set
  // in IntakeTab.jsx, not here: admin/general_manager see every agent's entries, manager/
  // agent_hr/agent are narrowed to their own (created_by_agent_id).
  // Hidden from general_manager (admin only): admin.ai, admin.history, admin.streets, admin.dedup
  // (dedup = the duplicate-listing review queue — verdicts reshape property identity
  // clusters market-wide, so admin-only for now). `manager` has NO admin.* caps at all.
  //
  // Granular controls INSIDE the general tab — general_manager sees General but not these:
  //   admin.general.offices           — add / edit / delete offices (OfficeEditor)
  //   admin.general.role_commissions  — agent roles + commissions (RoleCommissionEditor)
  //   admin.phones.delete             — the ✕ remove-number button (general_manager keeps add/edit/ban)
  //   admin.ext.force_hide            — Forced/Hidden setting controls + chip "forced" (extension config)
  //   agents.link_login               — AgentModal's "Scheduler login" picker (links an agent
  //                                      row to any app_users identity, incl. admin's).
  //                                      general_manager already has full agents CRUD (tab.agents)
  //                                      but must NOT self-serve this one field — RLS is
  //                                      authenticated_all, so this UI gate is the only thing
  //                                      stopping a general_manager from relinking any agent to
  //                                      the admin's login. Moot for `manager`, which has no
  //                                      admin.* caps at all.
  //   candidates.delete               — delete a candidate card (QuickMenu + modal, 2026-07-11).
  //                                      Admin-only via '*' — deliberately NOT granted to
  //                                      general_manager/manager/agent_hr even though they have
  //                                      tab.candidates: deletion erases the hiring history
  //                                      (candidate_logs timeline); reject/no-show cover every
  //                                      legitimate "remove from pipeline" need.
  //   listings.lock                   — the 🔒 lock/unlock chip on a Listings row (2026-07-22).
  //                                      It upserts `agent_license` = the REMOTE KILL-SWITCH that
  //                                      blocks an agent's senTavus extension login/refresh. agent
  //                                      and agent_hr have tab.listings for monitoring only and must
  //                                      NOT be able to revoke anyone's extension (incl. their own
  //                                      colleagues'). Without this cap ListingsTab passes no
  //                                      onToggleLock, so the chip isn't rendered at all.
  //                                      ⚠️ UI-only, like every cap here — RLS is authenticated_all,
  //                                      so agent_license is still writable by any logged-in client.
  //   agents.change_office            — AgentModal's Office select (2026-07-09). manager/
  //                                      agent_hr are locked to their OWN office app-wide (see
  //                                      the header office-picker lockout in App.jsx) — letting
  //                                      them reassign ANY agent's office would let them move
  //                                      people into/out of that lock, defeating the point.
  //                                      general_manager DOES get this one (unlike link_login)
  //                                      since office structure is a normal ops decision, not
  //                                      an identity-security one.
  //   agents.assign_privileged_role   — Assign Manager/Admin/Agent +HR/General Manager in
  //                                      AgentModal. General managers may structure the team;
  //                                      managers must not promote staff into privileged roles.
}

// ── Capability bundles for the shared Numbers / Office Configuration components ─────────────
// Components take these props and never look at a role name, so Admin → Office Numbers and
// Manager → Settings → Numbers are one component with two capability sets. The SERVER is the
// authority (scheduler 0015: RLS scopes rows, triggers refuse office moves and admin-only office
// fields); a control rendered here by mistake still gets a 42501 back.
//   phones.move_office      assign / move / remove a number's office          (admin only, via '*')
//   phones.edit             add, ban, plan, SIM type, note                     (admin, general_manager)
//   phones.assign_agent     hand a number to an agent / take it back          (+ manager)
//   offices.edit_core       name, location; create and delete                 (admin only)
//   offices.edit_security   allowed IPs, Wi-Fi, presence token                (admin only)
//   offices.edit_schedule   hours, default WFH + rest day, desk count (0016)  (+ general_manager, manager)
//   offices.connect_router  Connect to router / Use this router / Disconnect (0034) (+ general_manager, manager)
export function numberCapabilities(role) {
  return {
    canMoveNumbers:   can(role, 'phones.move_office'),
    canEditNumbers:   can(role, 'phones.edit'),
    canAssignAgents:  can(role, 'phones.assign_agent'),
    canDeleteNumbers: can(role, 'admin.phones.delete'),
  }
}

// ── Settings sub-tabs, and whether the Settings tab exists at all ────────────────────────────────
// Settings is the NARROW door for roles without the Admin panel. Each sub-tab names the Admin
// surface that already carries the same screen (`dupOf`); a role that holds it reaches it there, and
// when that leaves nothing, the Settings tab is not shown (owner 2026-09-16: admin has "the admin
// control panel — move it inside it"). One list, read by App.jsx (tab visibility) and SettingsTab.
export const SETTINGS_SUBS = [
  { id: 'notifications', cap: 'settings.notifications', dupOf: 'admin.notifications' },
  { id: 'numbers',       cap: 'settings.numbers',       dupOf: 'admin.phones' },
  { id: 'offices',       cap: 'settings.offices',       dupOf: 'admin.general.offices' },
  // 0039: "Your passwords on myhome.ge / ss.ge" — the portal logins of the number(s) the viewer HOLDS.
  { id: 'passwords',     cap: 'settings.passwords',     dupOf: 'admin.phones' },
]
export function settingsSubTabs(role) {
  return SETTINGS_SUBS.filter(s => can(role, s.cap) && !can(role, s.dupOf)).map(s => s.id)
}

// notify.manage_bot — set the company Telegram bot token (admin only, via '*'; telegram_bot_set_token
// refuses everyone else server-side).
export function officeCapabilities(role) {
  return {
    canEditOfficeCore:     can(role, 'offices.edit_core'),
    canEditOfficeSecurity: can(role, 'offices.edit_security'),
    canEditOfficeSchedule: can(role, 'offices.edit_schedule'),
    canConnectRouter:      can(role, 'offices.connect_router'),
  }
}

export function can(role, cap) {
  const caps = CAPS[role]
  if (!caps) return false
  if (caps === '*') return true
  return caps.has(cap)
}
